import {
  Alert,
  Button,
  Checkbox,
  Group,
  Modal,
  Space,
  Tooltip
} from "@mantine/core";
import { IconAlertTriangle, IconEye, IconPlus } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useEffect, useMemo, useState } from "react";
import { useDownload } from "../../../../hooks/useDownload";
import { IconButton } from "../../../../mantine/components/IconButton/IconButton";
import { ModalFooter } from "../../../../mantine/components/ModalFooter/ModalFooter";
import { getMinimalMRTOptions } from "../../../../mantine/getMinimalMRTOptions";
import { AlertPresets } from "../../../../mantine/mantinePresets";
import { useToast } from "../../../../mantine/notifications/useToast";
import urls from "../../../../urls";
import { FINANCIAL_DOCUMENT_TYPES } from "../../Paragraph6.constants";
import {
  FinancialDocumentType,
  type Paragraph6ContractResponse,
  type Paragraph6CreatableCredit
} from "../../Paragraph6.types";

const DATA_COMPLETE_WARNING_THRESHOLD = 100;
export interface Paragraph6AvailableCreditsModalProps {
  variantId: number;
  creatableCredits?: Array<Paragraph6CreatableCredit>;
  contracts?: Array<Paragraph6ContractResponse>;
  createParagraph6Credits: (
    credits: Array<Paragraph6CreatableCredit>,
    preview?: boolean
  ) => Promise<void>;
  fetchDraftCreditStatus: (
    credit: Paragraph6CreatableCredit
  ) => Promise<string>;
  previewMode?: boolean;
  isOpen?: boolean;
  onClose: () => void;
  contentType?: FinancialDocumentType;
}

function Paragraph6AvailableCreditsModal({
  variantId,
  contracts,
  creatableCredits,
  previewMode,
  isOpen,
  createParagraph6Credits,
  fetchDraftCreditStatus,
  onClose,
  contentType = FinancialDocumentType.Credit
}: Paragraph6AvailableCreditsModalProps) {
  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();
  const { download } = useDownload();

  const [isModalOpen, setIsModalOpen] = useState(isOpen);
  const [isAllLoading, setIsAllLoading] = useState(false);
  const [isWarningAccepted, setIsWarningAccepted] = useState(false);

  useEffect(() => {
    setIsModalOpen(isOpen);
  }, [isOpen]);

  const contractsMap = useMemo(() => {
    const map: Record<string, Paragraph6ContractResponse> = {};
    contracts?.forEach((contract) => {
      map[contract.id] = contract;
    });
    return map;
  }, [contracts]);

  const nonWarningCredits = creatableCredits?.filter(
    (credit) =>
      credit.data_completeness_percentage !== undefined &&
      !(credit.data_completeness_percentage < DATA_COMPLETE_WARNING_THRESHOLD)
  );

  const isSomeWarning = creatableCredits?.some(
    (credit) =>
      credit.data_completeness_percentage !== undefined &&
      credit.data_completeness_percentage < DATA_COMPLETE_WARNING_THRESHOLD
  );

  const allCreditsCount =
    (isWarningAccepted
      ? creatableCredits?.length
      : nonWarningCredits?.length) ?? 0;

  function handleClickCreateAll() {
    const creditsToCreate = isWarningAccepted
      ? creatableCredits
      : nonWarningCredits;

    if (!creditsToCreate) {
      return;
    }
    setIsAllLoading(true);
    createParagraph6Credits(creditsToCreate, previewMode)
      .then(() => {
        onClose();
      })
      .catch((error) => {
        showErrorToast(error);
      })
      .finally(() => {
        queryClient.invalidateQueries({
          queryKey: ["paragraph6Credits", { variantId }]
        });
        setIsAllLoading(false);
      });
  }

  async function handleClickCreate(credit: Paragraph6CreatableCredit) {
    return createParagraph6Credits([credit], previewMode)
      .then(async () => {
        if (previewMode) {
          const creditId = await fetchDraftCreditStatus(credit);
          download({
            downloadUrl:
              urls.apiWithoutCamelization.paragraph6CreditDocument(creditId)
          });
        } else {
          onClose();
        }
      })
      .catch((error) => {
        showErrorToast(error);
      })
      .finally(() => {
        if (!previewMode) {
          queryClient.invalidateQueries({
            queryKey: ["paragraph6Credits", { variantId }]
          });
        }
      });
  }

  const mrtTable = useMantineReactTable<Paragraph6CreatableCredit>({
    ...getMinimalMRTOptions(),
    enableSorting: true,
    columns: [
      {
        header: "Vertrag",
        accessorFn: (rowData) =>
          contractsMap[rowData.contract]?.name ?? "Unbekannt",
        enableCellHoverReveal: true
      },
      {
        header: "Zeitraum",
        accessorFn: (rowData) => `${rowData.start_date} – ${rowData.end_date}`,
        enableSorting: false
      },
      {
        header: "Gemeinde/Kommune",
        accessorFn: (rowData) =>
          contractsMap[rowData.contract]?.community_name ?? "Unbekannt"
      },
      {
        header: "Aktion",
        accessorKey: "action",
        enableSorting: false,
        Cell: ({ row }) => {
          const [creditIsLoading, setCreditIsLoading] = useState(isAllLoading);

          async function createCredit() {
            setCreditIsLoading(true);
            await handleClickCreate(row.original);
            setCreditIsLoading(false);
          }

          const isWarning =
            row.original.data_completeness_percentage !== undefined &&
            row.original.data_completeness_percentage <
              DATA_COMPLETE_WARNING_THRESHOLD;

          const valueWarning = row.original.data_completeness_percentage
            ? `Es sind nur ${Math.floor(row.original.data_completeness_percentage)} % vorhanden.`
            : "Es sind keine Energiedaten vorhanden.";

          return (
            <>
              {isWarning ? (
                <Tooltip label={`Unvollständige Energiedaten! ${valueWarning}`}>
                  <IconAlertTriangle />
                </Tooltip>
              ) : (
                <Space w={24} /> // size of IconAlertTriangle
              )}
              {previewMode ? (
                <IconButton
                  color="secondary"
                  disabled={creditIsLoading}
                  icon={IconEye}
                  loading={creditIsLoading}
                  variant="transparent"
                  onClick={createCredit}
                >
                  Anschauen
                </IconButton>
              ) : (
                <IconButton
                  color="primary"
                  disabled={
                    creditIsLoading || (isWarning && !isWarningAccepted)
                  }
                  icon={IconPlus}
                  loading={creditIsLoading}
                  variant="transparent"
                  onClick={createCredit}
                >
                  {`${FINANCIAL_DOCUMENT_TYPES[contentType]} erstellen`}
                </IconButton>
              )}
            </>
          );
        }
      }
    ],
    data: creatableCredits ?? []
  });

  return (
    <Modal
      className="Paragraph6AvailableCreditsModal"
      opened={isModalOpen || false}
      size="min(1200px, 70%)"
      title={
        contentType === FinancialDocumentType.Credit
          ? "Verfügbare Gutschriften"
          : "Verfügbare Zahlungsinformationen"
      }
      onClose={onClose}
    >
      <Alert mb="md" {...AlertPresets.info}>
        Bei gleichzeitiger Erstellung mehrerer Gutschriften mit
        unterschiedlichen Abrechnungszeiträumen wird die Nummerierung in der
        Reihenfolge der Fertigstellung vergeben, nicht nach Abrechnungszeitraum.
        Wenn Sie sicherstellen möchten, dass ältere Zeiträume eine niedrigere
        Nummer erhalten (z. B. 2023 vor 2024), erstellen Sie die Gutschriften
        bitte einzeln.
      </Alert>

      {isSomeWarning && !previewMode && (
        <Alert
          color={AlertPresets.warning.color}
          icon={AlertPresets.warning.icon}
          mb="md"
          title="Eingeschränkte Datenverfügbarkeit"
        >
          Bitte beachten Sie, dass für mindestens eine der Lokationen, die in
          die Berechnung einbezogen werden,{" "}
          <strong>weniger als 100% der Energiedaten</strong> verfügbar sind.
          <br />
          <br />
          Sie haben die Möglichkeit, durch Setzen eines Hakens die Erstellung
          Ihrer Gutschriften dennoch zu veranlassen. Wir möchten Sie jedoch
          darauf hinweisen, dass in diesem Fall das Risiko besteht, dass die
          Energiedaten im Falle einer Rückerstattung nicht vollständig
          übereinstimmen könnten.
          <br />
          <br />
          <Group justify="flex-end">
            <Checkbox
              checked={isWarningAccepted}
              label="Ich habe die Warnung gelesen und akzeptiere sie."
              onChange={() => setIsWarningAccepted(!isWarningAccepted)}
            />
          </Group>
        </Alert>
      )}
      {creatableCredits && <MantineReactTable table={mrtTable} />}
      <ModalFooter>
        <Button variant="default" onClick={onClose}>
          Schließen
        </Button>
        {!previewMode && (
          <Button
            color="primary"
            disabled={allCreditsCount === 0}
            loading={isAllLoading}
            onClick={handleClickCreateAll}
          >
            {`Alle ${allCreditsCount} ${
              contentType === FinancialDocumentType.Credit
                ? "Gutschriften"
                : "Zahlungsinformationen"
            } erstellen`}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}

export { Paragraph6AvailableCreditsModal };
